// Dashboard v1
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FaLink, FaUsers, FaGavel, FaCheckCircle, FaDollarSign, FaExclamationTriangle, FaPlus, FaUserEdit, FaFileAlt } from 'react-icons/fa';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, alertsRes] = await Promise.all([
        axios.get('/dashboard/stats', config),
        axios.get('/dashboard/alerts', config)
      ]);

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }

      if (alertsRes.data.success) {
        setAlerts(alertsRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="dashboard-subtitle">Overview of your bail bond operations</p>
      </div>

      {/* Start New Bond Button */}
      <div className="new-bond-action">
        <button
          className="btn-new-bond"
          onClick={() => navigate('/bonds/new')}
        >
          <FaPlus /> Start a New Bond
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div
          className="stat-card stat-primary"
          onClick={() => navigate('/bonds?status=active')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">
            <FaLink />
          </div>
          <div className="stat-content">
            <h3>Active Bonds</h3>
            <p className="stat-number">{stats?.bonds?.active_bonds || 0}</p>
            <p className="stat-detail">
              {formatCurrency(stats?.bonds?.active_bond_amount)} total
            </p>
          </div>
        </div>

        <div className="stat-card stat-success">
          <div className="stat-icon">
            <FaUsers />
          </div>
          <div className="stat-content">
            <h3>Active Defendants</h3>
            <p className="stat-number">{stats?.defendants?.active_defendants || 0}</p>
            <p className="stat-detail">
              {stats?.defendants?.total_defendants || 0} total
            </p>
          </div>
        </div>

        <div
          className="stat-card stat-danger"
          onClick={() => navigate('/defendants?incomplete=true')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">
            <FaUserEdit />
          </div>
          <div className="stat-content">
            <h3>Incomplete Defendant Profiles</h3>
            <p className="stat-number">{stats?.defendants?.incomplete_profiles || 0}</p>
            <p className="stat-detail">Missing critical information</p>
          </div>
        </div>

        <div
          className="stat-card stat-warning"
          onClick={() => navigate('/bonds?incomplete=true')}
          style={{ cursor: 'pointer' }}
        >
          <div className="stat-icon">
            <FaFileAlt />
          </div>
          <div className="stat-content">
            <h3>Bonds Missing Case/Court Info</h3>
            <p className="stat-number">{stats?.bonds?.incomplete_bonds || 0}</p>
            <p className="stat-detail">No case number or court assigned</p>
          </div>
        </div>

        <div className="stat-card stat-warning">
          <div className="stat-icon">
            <FaGavel />
          </div>
          <div className="stat-content">
            <h3>Upcoming Courts</h3>
            <p className="stat-number">{stats?.upcomingCourtDates?.upcoming_court_dates || 0}</p>
            <p className="stat-detail">Next 30 days</p>
          </div>
        </div>

        <div className="stat-card stat-info">
          <div className="stat-icon">
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <h3>Check-in Rate</h3>
            <p className="stat-number">{stats?.checkinCompliance?.compliance_rate || 0}%</p>
            <p className="stat-detail">
              {stats?.checkinCompliance?.completed_checkins || 0} of {stats?.checkinCompliance?.total_checkins || 0} completed
            </p>
          </div>
        </div>

        <div className="stat-card stat-money">
          <div className="stat-icon">
            <FaDollarSign />
          </div>
          <div className="stat-content">
            <h3>Outstanding Premium</h3>
            <p className="stat-number">{formatCurrency(stats?.bonds?.outstanding_premium)}</p>
            <p className="stat-detail">
              {formatCurrency(stats?.bonds?.premium_collected)} collected
            </p>
          </div>
        </div>

        <div className="stat-card stat-danger">
          <div className="stat-icon">
            <FaExclamationTriangle />
          </div>
          <div className="stat-content">
            <h3>Forfeited Bonds</h3>
            <p className="stat-number">{stats?.bonds?.forfeited_bonds || 0}</p>
            <p className="stat-detail">
              {stats?.checkinCompliance?.missed_checkins || 0} missed check-ins
            </p>
          </div>
        </div>
      </div>

      {/* Alerts Section */}
      <div className="alerts-section">
        <h2>Alerts & Notifications</h2>

        <div className="alerts-grid">
          {/* Upcoming Court Dates */}
          <div className="alert-card">
            <div className="alert-header">
              <FaGavel className="alert-icon" />
              <h3>Upcoming Court Dates (Next 3 Days)</h3>
            </div>
            <div className="alert-body">
              {alerts?.upcomingCourts?.length > 0 ? (
                <ul className="alert-list">
                  {alerts.upcomingCourts.slice(0, 5).map((court) => (
                    <li key={court.id} className="alert-item">
                      <div>
                        <strong>{court.defendant_name}</strong>
                        <p className="alert-detail">
                          {court.hearing_type} - {court.court_name}
                        </p>
                      </div>
                      <span className="alert-date">{formatDate(court.scheduled_date)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-alerts">No upcoming court dates in the next 3 days</p>
              )}
            </div>
          </div>

          {/* Outstanding Premiums */}
          <div className="alert-card">
            <div className="alert-header">
              <FaDollarSign className="alert-icon" />
              <h3>Outstanding Premiums</h3>
            </div>
            <div className="alert-body">
              {alerts?.outstandingPremiums?.length > 0 ? (
                <ul className="alert-list">
                  {alerts.outstandingPremiums.slice(0, 5).map((premium) => (
                    <li key={premium.id} className="alert-item">
                      <div>
                        <strong>{premium.defendant_name}</strong>
                        <p className="alert-detail">Power #{premium.bond_number}</p>
                      </div>
                      <span className="alert-amount">{formatCurrency(premium.amount_due)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-alerts">All premiums are up to date</p>
              )}
            </div>
          </div>

          {/* Missed Check-ins */}
          <div className="alert-card">
            <div className="alert-header">
              <FaExclamationTriangle className="alert-icon" />
              <h3>Missed Check-ins (Last 24 Hours)</h3>
            </div>
            <div className="alert-body">
              {alerts?.missedCheckins?.length > 0 ? (
                <ul className="alert-list">
                  {alerts.missedCheckins.slice(0, 5).map((checkin, index) => (
                    <li key={index} className="alert-item">
                      <div>
                        <strong>{checkin.defendant_name}</strong>
                        <p className="alert-detail">
                          Power #{checkin.bond_number} - {checkin.frequency} check-in
                        </p>
                      </div>
                      <span className="badge badge-forfeited">MISSED</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-alerts">No missed check-ins in the last 24 hours</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <button className="btn btn-primary" onClick={() => window.location.href = '/defendants/new'}>
            + New Defendant
          </button>
          <button className="btn btn-primary" onClick={() => window.location.href = '/bonds/new'}>
            + New Bond
          </button>
          <button className="btn btn-secondary" onClick={() => window.location.href = '/court-dates'}>
            View Calendar
          </button>
          <button className="btn btn-secondary" onClick={() => window.location.href = '/checkins'}>
            Check-in Monitor
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
